import { useToggle } from '@alluradirect/react';
import { Grid, Link, Typography } from '@mui/material';
import { AppDivider } from 'components/core/AppDivider/AppDivider';
import LoadingSkeleton from 'components/core/LoadingSkeleton/LoadingSkeleton';
import { usePropertyCancellationPolicy } from 'hooks/api/property/queries/usePropertyCancellationPolicy';
import { useMemo } from 'react';
import { CancellationPolicyDialog } from 'scenes/guest/views/BookingDetail/components/CancellationRequestDialog/components/CancellationPolicyDialog';
import styled from 'styled-components';
import { usePropertyInformationQuery } from 'components/views/pages/Checkout/hooks/usePropertyInformationQuery';

const StyledLink = styled(Link)`
  &:hover {
    cursor: pointer;
  }
`;

interface Props {
  isLoading: boolean;
  pid: string | undefined;
}

// TODO: cancellation policy data
export const CheckoutSummaryCommonQuestions = ({ isLoading, pid }: Props) => {
  const cancellationToggle = useToggle();
  const propertyCancellationQuery = usePropertyCancellationPolicy({ pid: Number(pid) });
  const propertyInfo = usePropertyInformationQuery({ pid: Number(pid), enabled: Boolean(pid) });
  const maxGuestCapacity = propertyInfo.data?.DATA.MAX_CAPACITY;
  const petsAllowed = propertyInfo.data?.DATA.IS_PET_FRIENDLY;

  const petsText = useMemo(() => {
    if (petsAllowed) {
      return 'Yes, you can bring your pet';
    }
    return 'Sorry no, pets are not allowed at this property';
  }, [petsAllowed]);

  const data = propertyCancellationQuery.data?.DATA?.CANCELLATION_POLICY;
  const dateRanges = data?.DATE_RANGES;
  const description = data?.DESCRIPTION;
  const isSpecialCircumstance = data?.IS_SPECIAL_CIRCUMSTANCE;
  const specialCircumstanceDescription = data?.SPECIAL_CIRCUMSTANCE_DESCRIPTION;

  return (
    <>
      <Grid item xs={12}>
        <AppDivider marginVertical={'24px'} />
        <Typography variant="h5" mb={2}>
          Common Questions
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong>How Many People Can Stay Here?</strong>
            </Typography>
            <LoadingSkeleton isLoading={isLoading}>
              <Typography variant="body2">The Max Group Size is: {maxGuestCapacity}</Typography>
            </LoadingSkeleton>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong>Can I Bring My Pet?</strong>
            </Typography>
            <LoadingSkeleton isLoading={isLoading}>
              <Typography variant="body2">{petsText}</Typography>
            </LoadingSkeleton>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong>Is there a Discount for Longer Stays?</strong>
            </Typography>
            <Typography variant="body2">
              Our prices will automatically include any available discounts when you enter your dates
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1">
              <strong>What are Your Cancellation Policies?</strong>
            </Typography>
            <StyledLink onClick={cancellationToggle.open}>
              <Typography variant="body2">Click to View Cancellation Policy</Typography>
            </StyledLink>
          </Grid>
        </Grid>
      </Grid>

      {cancellationToggle.isOpen && propertyCancellationQuery.data ? (
        <CancellationPolicyDialog
          isOpen={cancellationToggle.isOpen}
          close={cancellationToggle.close}
          dateRanges={dateRanges}
          description={description}
          isSpecialCircumstance={isSpecialCircumstance}
          specialCircumstanceDescription={specialCircumstanceDescription}
        />
      ) : null}
    </>
  );
};
